import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { getCart, updateCartQuantity, removeFromCart, clearCart, updateCartNote } from '../data/mock';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Gift, Check, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const FREE_DRINK_OPTIONS = [
  { id: 'free-coca', name: 'Coca Cola 1.25L' },
  { id: 'free-coca-zero', name: 'Coca Zero 1.25L' },
  { id: 'free-lipton', name: 'Lipton 1.25L' },
];

const Cart = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [total, setTotal] = useState(0);
  const [promotion, setPromotion] = useState('');
  const [hasFreeDrink, setHasFreeDrink] = useState(false);
  const [selectedFreeDrink, setSelectedFreeDrink] = useState(null);

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = () => {
    const currentCart = getCart();
    setCart(currentCart);
    calculateTotal(currentCart);
  };

  const getPizzaCount = (cartItems) => {
    return cartItems
      .filter(item => item.category && !['boisson', 'salade', 'dessert'].includes(item.category))
      .reduce((acc, item) => acc + item.quantity, 0);
  };

  const calculateTotal = (cartItems) => {
    let sum = cartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
    let promotionMessage = '';
    let freeDrink = false;
    
    const pizzaCount = getPizzaCount(cartItems);
    const fromageCount = cartItems.filter(item => item.name === "Fromage").reduce((acc, item) => acc + item.quantity, 0);
    
    // Offre 3 pizzas Fromage = 22.50€
    if (fromageCount >= 3) {
      const sets = Math.floor(fromageCount / 3);
      const fromageItem = cartItems.find(item => item.name === "Fromage");
      if (fromageItem) {
        const normalPrice = fromageItem.price * 3 * sets;
        const promoPrice = 22.50 * sets;
        sum = sum - normalPrice + promoPrice;
        promotionMessage = `Offre 3 Pizzas Fromage appliquée ! Économie de ${(normalPrice - promoPrice).toFixed(2)}€`;
      }
    }
    
    // Offre 3+ pizzas = 1 boisson 1.25L offerte
    if (pizzaCount >= 3 && pizzaCount < 10) {
      freeDrink = true;
    }
    
    // Offre 10 pizzas = 89.90€ (les pizzas au-dessus de 10 sont au prix normal)
    if (pizzaCount >= 10) {
      const nonPizzaSum = cartItems
        .filter(item => item.category && ['boisson', 'salade', 'dessert'].includes(item.category))
        .reduce((acc, item) => acc + (item.price * item.quantity), 0);
      
      // Calcul du prix des pizzas au-delà de 10
      const pizzaItems = cartItems.filter(item => !item.category || !['boisson', 'salade', 'dessert'].includes(item.category));
      let extraPizzaSum = 0;
      let counted = 0;
      for (const item of pizzaItems) {
        if (counted + item.quantity <= 10) {
          counted += item.quantity;
        } else {
          const inDeal = Math.max(0, 10 - counted);
          const extras = item.quantity - inDeal;
          extraPizzaSum += extras * item.price;
          counted += item.quantity;
        }
      }
      
      sum = 89.90 + extraPizzaSum + nonPizzaSum;
      const extraMsg = extraPizzaSum > 0 ? ` + ${extraPizzaSum.toFixed(2)}€ (${pizzaCount - 10} pizza(s) supplémentaire(s))` : '';
      promotionMessage = `OFFRE SPÉCIALE 10 PIZZAS ! Prix fixe : 89.90€${extraMsg}`;
      freeDrink = false;
    }
    
    setTotal(sum);
    setPromotion(promotionMessage);
    setHasFreeDrink(freeDrink);

    // Reset selected drink if no longer eligible
    if (!freeDrink) {
      setSelectedFreeDrink(null);
      localStorage.removeItem('casaDelPizzaFreeDrink');
    }
  };

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) {
      handleRemove(itemId);
      return;
    }
    const updatedCart = updateCartQuantity(itemId, newQuantity);
    setCart(updatedCart);
    calculateTotal(updatedCart);
    window.dispatchEvent(new Event('storage'));
  };

  const handleRemove = (itemId) => {
    const updatedCart = removeFromCart(itemId);
    setCart(updatedCart);
    calculateTotal(updatedCart);
    window.dispatchEvent(new Event('storage'));
    toast.success('Produit retiré du panier');
  };

  const handleClearCart = () => {
    clearCart();
    setCart([]);
    setTotal(0);
    setSelectedFreeDrink(null);
    window.dispatchEvent(new Event('storage'));
    toast.success('Panier vidé');
  };

  const handleSelectFreeDrink = (drink) => {
    setSelectedFreeDrink(drink);
    localStorage.setItem('casaDelPizzaFreeDrink', JSON.stringify(drink));
    toast.success(`${drink.name} offerte ajoutée !`);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Votre panier est vide');
      return;
    }
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="bg-black min-h-screen pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center py-20">
            <ShoppingBag className="mx-auto text-gray-700 mb-6" size={80} />
            <h1 className="text-4xl font-bold text-white mb-4" data-testid="empty-cart-title">Votre Panier est Vide</h1>
            <p className="text-gray-400 text-lg mb-8">
              Découvrez nos délicieuses pizzas au feu de bois
            </p>
            <Link to="/menu">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold text-lg px-8"
                data-testid="go-to-menu-btn"
              >
                Voir le Menu
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const pizzaCount = getPizzaCount(cart);

  return (
    <div className="bg-black min-h-screen pt-32 pb-16">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-5xl font-bold text-white mb-8" data-testid="cart-title">
            Votre <span className="text-red-600">Panier</span>
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item) => (
                <div 
                  key={item.id}
                  data-testid={`cart-item-${item.id}`}
                  className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-xl p-6 hover:border-red-600/50 transition-all duration-300"
                >
                  <div className="flex gap-4">
                    {item.image && (
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )}
                    
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="text-xl font-bold text-white mb-1">
                            {item.customName || item.name}
                            {item.chausson && !item.customName?.includes('Chausson') && !item.name?.includes('Chausson') && (
                              <span className="text-red-400 text-sm ml-2">(Chausson)</span>
                            )}
                          </h3>
                          {item.ingredients && (
                            <p className="text-gray-400 text-sm">{item.ingredients}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemove(item.id)}
                          className="text-gray-400 hover:text-red-500 hover:bg-red-500/10"
                          data-testid={`remove-item-${item.id}`}
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>

                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-2 bg-gray-800 rounded-lg p-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                            className="text-white hover:bg-gray-700 h-8 w-8 p-0"
                            data-testid={`decrease-qty-${item.id}`}
                          >
                            <Minus size={16} />
                          </Button>
                          <span className="text-white font-semibold w-8 text-center" data-testid={`qty-${item.id}`}>
                            {item.quantity}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            className="text-white hover:bg-gray-700 h-8 w-8 p-0"
                            data-testid={`increase-qty-${item.id}`}
                          >
                            <Plus size={16} />
                          </Button>
                        </div>

                        <div className="text-right">
                          <div className="text-2xl font-bold text-red-500" data-testid={`item-total-${item.id}`}>
                            {(item.price * item.quantity).toFixed(2)}€
                          </div>
                          <div className="text-sm text-gray-500">
                            {item.price.toFixed(2)}€ / unité
                          </div>
                        </div>
                      </div>

                      {/* Note / Modification */}
                      <div className="mt-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <MessageSquare size={13} className="text-gray-500" />
                          <span className="text-xs text-gray-500">Note / Modification</span>
                        </div>
                        <input
                          type="text"
                          placeholder="Ex: sans oignons, bien cuite..."
                          value={item.note || ''}
                          onChange={(e) => {
                            const updated = updateCartNote(item.id, e.target.value);
                            setCart(updated);
                          }}
                          className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-red-500/50 focus:outline-none transition-colors"
                          data-testid={`note-${item.id}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Free Drink Selection */}
              {hasFreeDrink && (
                <div 
                  className="bg-gradient-to-br from-green-900/30 to-black border-2 border-green-500/50 rounded-xl p-6"
                  data-testid="free-drink-section"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-green-600/20 p-2 rounded-lg">
                      <Gift className="text-green-400" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Boisson 1.25L OFFERTE !</h3>
                      <p className="text-green-400 text-sm">{pizzaCount} pizzas dans votre panier - choisissez votre boisson gratuite</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {FREE_DRINK_OPTIONS.map((drink) => {
                      const isSelected = selectedFreeDrink?.id === drink.id;
                      return (
                        <button
                          key={drink.id}
                          onClick={() => handleSelectFreeDrink(drink)}
                          data-testid={`free-drink-${drink.id}`}
                          className={`relative flex items-center gap-3 p-4 rounded-xl border-2 transition-all duration-300 ${
                            isSelected
                              ? 'border-green-500 bg-green-600/20 shadow-lg shadow-green-600/20'
                              : 'border-gray-700 bg-gray-900/50 hover:border-green-500/50 hover:bg-gray-800'
                          }`}
                        >
                          {isSelected && (
                            <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-1">
                              <Check size={14} className="text-white" />
                            </div>
                          )}
                          <span className="text-2xl">🥤</span>
                          <div className="text-left">
                            <p className={`font-bold text-sm ${isSelected ? 'text-green-300' : 'text-white'}`}>
                              {drink.name}
                            </p>
                            <p className="text-green-400 font-bold text-xs">GRATUIT</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {!selectedFreeDrink && (
                    <p className="text-yellow-400 text-sm mt-3 font-semibold text-center animate-pulse">
                      Sélectionnez votre boisson offerte ci-dessus
                    </p>
                  )}
                </div>
              )}

              {/* Selected free drink display */}
              {selectedFreeDrink && hasFreeDrink && (
                <div 
                  className="bg-gradient-to-br from-gray-900 to-black border border-green-500/50 rounded-xl p-6"
                  data-testid="selected-free-drink-item"
                >
                  <div className="flex gap-4 items-center">
                    <div className="w-24 h-24 bg-green-600/20 rounded-lg flex items-center justify-center">
                      <span className="text-5xl">🥤</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="text-xl font-bold text-white mb-1">{selectedFreeDrink.name}</h3>
                          <span className="inline-block bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                            OFFERTE
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-400">0.00€</div>
                          <div className="text-sm text-gray-500 line-through">3.50€</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <Button
                variant="outline"
                onClick={handleClearCart}
                className="w-full border-red-600/50 text-red-500 hover:bg-red-600/10 hover:text-red-400"
                data-testid="clear-cart-btn"
              >
                <Trash2 size={18} className="mr-2" />
                Vider le Panier
              </Button>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-gray-900 to-black border border-red-600/30 rounded-xl p-6 sticky top-32" data-testid="order-summary">
                <h2 className="text-2xl font-bold text-white mb-6">Résumé</h2>

                {promotion && (
                  <div className="bg-red-600/20 border border-red-500/50 rounded-lg p-4 mb-6 animate-pulse">
                    <p className="text-red-400 font-semibold text-sm whitespace-pre-line" data-testid="promotion-message">{promotion}</p>
                  </div>
                )}

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-gray-400">
                    <span>Sous-total</span>
                    <span className="font-semibold" data-testid="subtotal">{total.toFixed(2)}€</span>
                  </div>
                  {selectedFreeDrink && hasFreeDrink && (
                    <div className="flex justify-between text-green-400">
                      <span>{selectedFreeDrink.name}</span>
                      <span className="font-semibold">OFFERT</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-400">
                    <span>Livraison</span>
                    <span className="font-semibold text-green-500">Gratuite</span>
                  </div>
                  <div className="border-t border-gray-800 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-white">Total</span>
                      <span className="text-3xl font-bold text-red-500" data-testid="cart-total">{total.toFixed(2)}€</span>
                    </div>
                  </div>
                </div>

                <Button 
                  size="lg"
                  onClick={handleCheckout}
                  className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold text-lg py-6 shadow-lg shadow-red-600/30 mb-4"
                  data-testid="checkout-btn"
                >
                  Commander
                  <ArrowRight size={20} className="ml-2" />
                </Button>

                <Link to="/menu">
                  <Button 
                    variant="outline"
                    size="lg"
                    className="w-full border-gray-700 text-gray-300 hover:bg-gray-900 hover:text-white"
                    data-testid="continue-shopping-btn"
                  >
                    Continuer mes Achats
                  </Button>
                </Link>

                <div className="mt-6 pt-6 border-t border-gray-800">
                  <div className="bg-red-600/20 border border-red-500/50 rounded-lg p-4">
                    <p className="text-red-400 font-semibold text-sm text-center">
                      Livraison Gratuite à Marseille
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
